import { formatCpf } from "@/lib/formatters";
import { useState, useEffect } from "react";
import { useLocation, useSearch, Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/lib/supabase";
import { apiRequest } from "@/lib/queryClient";
import {
  Loader2,
  CheckCircle,
  ArrowLeft,
  Building2,
  Eye,
  EyeOff,
} from "lucide-react";
import logoImg from "@/assets/images/logo.png";
import { formatCnpj, formatPhone } from "@/lib/formatters";

const UF_LIST = [
  "AC",
  "AL",
  "AP",
  "AM",
  "BA",
  "CE",
  "DF",
  "ES",
  "GO",
  "MA",
  "MT",
  "MS",
  "MG",
  "PA",
  "PB",
  "PR",
  "PE",
  "PI",
  "RJ",
  "RN",
  "RS",
  "RO",
  "RR",
  "SC",
  "SP",
  "SE",
  "TO",
];

type Step = "code" | "form" | "success";

export default function Registrar() {
  const search = useSearch();
  const params = new URLSearchParams(search);
  const codeFromUrl = params.get("code") || "";

  const [, setLocation] = useLocation();
  const { toast } = useToast();

  const [step, setStep] = useState<Step>(codeFromUrl ? "code" : "code");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [code, setCode] = useState(codeFromUrl);
  const [codeInfo, setCodeInfo] = useState<{
    tipo: string;
    vistoriadoraId?: string;
    artMultipla?: boolean;
  } | null>(null);

  const [cnpj, setCnpj] = useState("");
  const [razaoSocial, setRazaoSocial] = useState("");
  const [nomeFantasia, setNomeFantasia] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [nome, setNome] = useState("");
  const [cpf, setCpf] = useState("");
  const [uf, setUf] = useState("");
  const [cidade, setCidade] = useState("");
  const [cep, setCep] = useState("");
  const [cepLoading, setCepLoading] = useState(false);
  const [logradouro, setLogradouro] = useState("");
  const [numero, setNumero] = useState("");
  const [bairro, setBairro] = useState("");
  const [telefone, setTelefone] = useState("");
  const [tagEmpresa, setTagEmpresa] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  useEffect(() => {
    if (codeFromUrl) {
      handleValidateCode(codeFromUrl);
    }
  }, []);

  const handleCepChange = async (value: string) => {
    const digits = value.replace(/\D/g, "");
    const formatted =
      digits.length > 5
        ? `${digits.slice(0, 5)}-${digits.slice(5, 8)}`
        : digits;
    setCep(formatted);
    if (digits.length === 8) {
      setCepLoading(true);
      try {
        const res = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
        const data = await res.json();
        if (!data.erro) {
          setUf(data.uf || "");
          setCidade(data.localidade || "");
          setLogradouro(data.logradouro || "");
          setBairro(data.bairro || "");
        }
      } catch {
        // silently ignore CEP lookup errors
      } finally {
        setCepLoading(false);
      }
    }
  };

  const handleValidateCode = async (codeValue?: string) => {
    const codeToValidate = codeValue || code;
    if (!codeToValidate.trim()) {
      setError("Digite o código de convite");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await apiRequest("POST", "/api/invite-codes/validate", {
        code: codeToValidate.trim(),
      });
      const data = await res.json();
      if (data.valid) {
        setCodeInfo({
          tipo: data.tipo,
          vistoriadoraId: data.vistoriadoraId,
          artMultipla: data.artMultipla,
        });
        setStep("form");
      } else {
        setError("Código inválido ou expirado");
      }
    } catch (err: any) {
      setError("Código inválido ou expirado");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (password !== confirmPassword) {
      setError("As senhas não conferem");
      return;
    }
    if (password.length < 6) {
      setError("A senha deve ter pelo menos 6 caracteres");
      return;
    }
    const isVistoriadora = codeInfo?.tipo === "VISTORIADORA";
    if (isVistoriadora && !tagEmpresa.trim()) {
      setError("Tag é obrigatória para vistoriadora");
      return;
    }
    if (!cnpj.replace(/\D/g, "") || cnpj.replace(/\D/g, "").length < 14) {
      setError("CNPJ inválido. Digite os 14 dígitos");
      return;
    }
    if (!uf) {
      setError("Selecione o estado (UF)");
      return;
    }
    if (isVistoriadora && !cidade.trim()) {
      setError("Digite a cidade");
      return;
    }

    setLoading(true);
    try {
      await apiRequest("POST", "/api/auth/register", {
        email,
        password,
        nome,
        cpf: cpf.replace(/\D/g, ""),
        codigoConvite: code.trim(),
        empresa: {
          razaoSocial,
          nomeFantasia: nomeFantasia || undefined,
          cnpj: cnpj.replace(/\D/g, ""),
          uf,
          cidade: cidade || undefined,
          cep: cep.replace(/\D/g, "") || undefined,
          logradouro: logradouro || undefined,
          numero: numero || undefined,
          bairro: bairro || undefined,
          telefone: telefone || undefined,
          tagEmpresa: tagEmpresa.toUpperCase() || undefined,
        },
      });

      // Cadastro por convite já cria a conta ativa: entra direto.
      await supabase.auth.signInWithPassword({ email, password });

      setStep("success");
    } catch (err: any) {
      let message = "Erro ao processar cadastro";
      try {
        const parsed = JSON.parse(err.message.split(": ").slice(1).join(": "));
        message = parsed.message || message;
      } catch {
        if (err.message.includes(":")) {
          message = err.message.split(": ").slice(1).join(": ");
        }
      }
      setError(message);
      toast({
        title: "Erro no Cadastro",
        description: message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (step === "success") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100 p-4">
        <div className="w-full max-w-md">
          <Card className="p-8 border-none shadow-2xl text-center">
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
            </div>
            <h1
              className="text-2xl font-bold font-display text-slate-900 mb-2"
              data-testid="text-register-success"
            >
              Cadastro Realizado!
            </h1>
            <p className="text-muted-foreground mb-6">
              Sua empresa e usuário foram criados com sucesso. Agora você pode
              acessar o painel.
            </p>
            <Button
              className="w-full gap-2"
              onClick={() => setLocation("/dashboard")}
              data-testid="button-go-dashboard"
            >
              Acessar o Painel
              <ArrowLeft className="w-4 h-4 rotate-180" />
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-100 p-4">
      <div className="w-full max-w-lg">
        <div className="flex justify-center mb-6">
          <Link href="/">
            <img
              src={logoImg}
              alt="ReMonta"
              className="h-14 w-14 object-contain cursor-pointer"
              data-testid="img-logo-register"
            />
          </Link>
        </div>

        <Card className="p-8 border-none shadow-2xl">
          {step === "code" && (
            <>
              <div className="text-center mb-6">
                <h1
                  className="text-2xl font-bold font-display text-slate-900"
                  data-testid="text-register-title"
                >
                  Cadastro de Empresa
                </h1>
                <p className="text-muted-foreground mt-2">
                  Insira o código de convite que você recebeu para iniciar o
                  cadastro.
                </p>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="invite-code">Código de Convite</Label>
                  <Input
                    id="invite-code"
                    placeholder="Ex: A1B2C3D4"
                    value={code}
                    onChange={(e) => setCode(e.target.value.toUpperCase())}
                    className="bg-slate-50 border-slate-200 text-center text-lg tracking-widest font-mono"
                    data-testid="input-invite-code"
                  />
                </div>

                {error && (
                  <div
                    className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm"
                    data-testid="text-error"
                  >
                    {error}
                  </div>
                )}

                <Button
                  className="w-full gap-2"
                  onClick={() => handleValidateCode()}
                  disabled={loading || !code.trim()}
                  data-testid="button-validate-code"
                >
                  {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                  Validar Código
                </Button>

                <div className="text-center pt-2">
                  <Link
                    href="/login"
                    className="text-sm text-primary hover:underline"
                    data-testid="link-back-login"
                  >
                    Já tem uma conta? Entrar
                  </Link>
                </div>
              </div>
            </>
          )}

          {step === "form" && codeInfo && (
            <>
              <div className="text-center mb-6">
                <div className="flex justify-center mb-3">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Building2 className="w-6 h-6 text-primary" />
                  </div>
                </div>
                <h1
                  className="text-2xl font-bold font-display text-slate-900"
                  data-testid="text-form-title"
                >
                  Dados da Empresa
                </h1>
                <p className="text-muted-foreground mt-1 text-sm">
                  Cadastro como{" "}
                  <strong>
                    {codeInfo.tipo === "OWNER"
                      ? "Empresa (CEO/Owner)"
                      : codeInfo.tipo === "PARCEIRO_DE_NEGOCIOS"
                        ? "Solicitante"
                        : "Vistoriadora"}
                  </strong>
                </p>
              </div>

              <form
                onSubmit={handleRegister}
                className="space-y-4"
                data-testid="form-register"
              >
                <div className="space-y-2">
                  <Label htmlFor="cnpj">CNPJ *</Label>
                  <Input
                    id="cnpj"
                    placeholder="00.000.000/0000-00"
                    value={cnpj}
                    onChange={(e) => setCnpj(formatCnpj(e.target.value))}
                    className="bg-slate-50 border-slate-200"
                    required
                    data-testid="input-cnpj"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="razao-social">Razão Social *</Label>
                  <Input
                    id="razao-social"
                    placeholder="Nome da empresa conforme CNPJ"
                    value={razaoSocial}
                    onChange={(e) => setRazaoSocial(e.target.value)}
                    className="bg-slate-50 border-slate-200"
                    required
                    data-testid="input-razao-social"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="nome-fantasia">Nome Fantasia</Label>
                  <Input
                    id="nome-fantasia"
                    placeholder="Nome comercial (opcional)"
                    value={nomeFantasia}
                    onChange={(e) => setNomeFantasia(e.target.value)}
                    className="bg-slate-50 border-slate-200"
                    data-testid="input-nome-fantasia"
                  />
                </div>

                {codeInfo?.tipo === "VISTORIADORA" && (
                  <div className="space-y-2">
                    <Label htmlFor="cep">CEP</Label>
                    <div className="relative">
                      <Input
                        id="cep"
                        placeholder="00000-000"
                        value={cep}
                        onChange={(e) => handleCepChange(e.target.value)}
                        className="bg-slate-50 border-slate-200"
                        maxLength={9}
                        data-testid="input-cep"
                      />
                      {cepLoading && (
                        <Loader2 className="w-4 h-4 animate-spin absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Informe o CEP para preencher automaticamente o endereço
                    </p>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="uf">Estado (UF) *</Label>
                    <Select value={uf} onValueChange={setUf}>
                      <SelectTrigger
                        className="bg-slate-50 border-slate-200"
                        data-testid="select-uf"
                      >
                        <SelectValue placeholder="UF" />
                      </SelectTrigger>
                      <SelectContent>
                        {UF_LIST.map((u) => (
                          <SelectItem key={u} value={u}>
                            {u}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="cidade">
                      Cidade {codeInfo?.tipo === "VISTORIADORA" ? "*" : ""}
                    </Label>
                    <Input
                      id="cidade"
                      placeholder="Nome da cidade"
                      value={cidade}
                      onChange={(e) => setCidade(e.target.value)}
                      className="bg-slate-50 border-slate-200"
                      required={codeInfo?.tipo === "VISTORIADORA"}
                      data-testid="input-cidade"
                    />
                  </div>
                </div>

                {codeInfo?.tipo === "VISTORIADORA" && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="logradouro">Logradouro</Label>
                      <Input
                        id="logradouro"
                        placeholder="Rua, Avenida..."
                        value={logradouro}
                        onChange={(e) => setLogradouro(e.target.value)}
                        className="bg-slate-50 border-slate-200"
                        data-testid="input-logradouro"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="numero">Número</Label>
                        <Input
                          id="numero"
                          placeholder="Nº"
                          value={numero}
                          onChange={(e) => setNumero(e.target.value)}
                          className="bg-slate-50 border-slate-200"
                          data-testid="input-numero"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="bairro">Bairro</Label>
                        <Input
                          id="bairro"
                          placeholder="Bairro"
                          value={bairro}
                          onChange={(e) => setBairro(e.target.value)}
                          className="bg-slate-50 border-slate-200"
                          data-testid="input-bairro"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="telefone">Telefone</Label>
                      <Input
                        id="telefone"
                        placeholder="(00) 00000-0000"
                        value={telefone}
                        onChange={(e) =>
                          setTelefone(formatPhone(e.target.value))
                        }
                        className="bg-slate-50 border-slate-200"
                        data-testid="input-telefone"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="tag-empresa">
                        Tag para Identificação de Laudos *
                      </Label>
                      <p className="text-xs text-muted-foreground mb-2">
                        Código único usado para identificar os laudos. Ex: Se
                        sua tag é <strong>REM</strong>, os laudos serão:
                        RM-LA-REM-0001, RM-LA-REM-0002, etc.
                      </p>
                      <Input
                        id="tag-empresa"
                        placeholder="Ex: REM(RM-LA-REM-0001)"
                        value={tagEmpresa}
                        onChange={(e) =>
                          setTagEmpresa(e.target.value.toUpperCase())
                        }
                        className="bg-slate-50 border-slate-200"
                        maxLength={10}
                        required
                        data-testid="input-tag-empresa"
                      />
                    </div>
                  </>
                )}

                {codeInfo?.tipo !== "VISTORIADORA" && (
                  <div className="space-y-2">
                    <Label htmlFor="tag-empresa">
                      Tag para Identificação de Laudos
                    </Label>
                    <p className="text-xs text-muted-foreground mb-2">
                      Opcional. Se definida, seus laudos ficam: RM-LA-{"{REM}"}
                      -0001.
                    </p>
                    <Input
                      id="tag-empresa"
                      placeholder="Ex: REM"
                      value={tagEmpresa}
                      onChange={(e) =>
                        setTagEmpresa(e.target.value.toUpperCase())
                      }
                      className="bg-slate-50 border-slate-200"
                      maxLength={10}
                      data-testid="input-tag-empresa-solicitante"
                    />
                  </div>
                )}

                {codeInfo?.tipo !== "VISTORIADORA" && (
                  <div className="space-y-2">
                    <Label htmlFor="telefone">Telefone</Label>
                    <Input
                      id="telefone"
                      placeholder="(00) 00000-0000"
                      value={telefone}
                      onChange={(e) => setTelefone(formatPhone(e.target.value))}
                      className="bg-slate-50 border-slate-200"
                      data-testid="input-telefone"
                    />
                  </div>
                )}

                <div className="border-t border-slate-200 pt-4 mt-4">
                  <p className="text-sm font-medium text-slate-700 mb-3">
                    Dados de acesso do responsável
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="nome-responsavel">Nome Completo *</Label>
                  <Input
                    id="nome-responsavel"
                    placeholder="Nome do responsável pela empresa"
                    value={nome}
                    onChange={(e) => setNome(e.target.value)}
                    className="bg-slate-50 border-slate-200"
                    required
                    data-testid="input-nome"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="responsavel-cpf">CPF do usuário *</Label>
                  <Input id="responsavel-cpf" value={cpf} onChange={(e) => setCpf(formatCpf(e.target.value))} placeholder="000.000.000-00" maxLength={14} required data-testid="input-responsavel-cpf" />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">E-mail *</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="contato@empresa.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="bg-slate-50 border-slate-200"
                    required
                    data-testid="input-email"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="password">Senha *</Label>
                    <div className="relative">
                      <Input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Min. 6 caracteres"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="bg-slate-50 border-slate-200 pr-10"
                        required
                        data-testid="input-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((v) => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        tabIndex={-1}
                        data-testid="button-toggle-password"
                      >
                        {showPassword ? (
                          <EyeOff className="w-4 h-4" />
                        ) : (
                          <Eye className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirm-password">Confirmar Senha *</Label>
                    <div className="relative">
                      <Input
                        id="confirm-password"
                        type={showConfirmPassword ? "text" : "password"}
                        placeholder="Repita a senha"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="bg-slate-50 border-slate-200 pr-10"
                        required
                        data-testid="input-confirm-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword((v) => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        tabIndex={-1}
                        data-testid="button-toggle-confirm-password"
                      >
                        {showConfirmPassword ? (
                          <EyeOff className="w-4 h-4" />
                        ) : (
                          <Eye className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                {error && (
                  <div
                    className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm"
                    data-testid="text-error"
                  >
                    {error}
                  </div>
                )}

                <Button
                  type="submit"
                  className="w-full gap-2 mt-2"
                  disabled={loading}
                  data-testid="button-register"
                >
                  {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                  Cadastrar Empresa
                </Button>

                <div className="flex items-center justify-between pt-2">
                  <button
                    type="button"
                    onClick={() => {
                      setStep("code");
                      setError("");
                    }}
                    className="text-sm text-muted-foreground hover:text-slate-700"
                    data-testid="button-back-code"
                  >
                    ← Trocar código
                  </button>
                  <Link
                    href="/login"
                    className="text-sm text-primary hover:underline"
                    data-testid="link-back-login"
                  >
                    Já tem conta? Entrar
                  </Link>
                </div>
              </form>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
